import React, { useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { App, Typography, Form, Input, InputNumber, Button, Select, Row, Col, Alert, Card, Switch } from 'antd'
import {
  LoadingOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  WarningOutlined,
  EnvironmentOutlined,
  FieldTimeOutlined,
  ScheduleOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  CalendarOutlined,
} from '@ant-design/icons'
import { useIntl } from 'react-intl'
import uniqBy from 'lodash/uniqBy'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import * as logger from '../../services/logger'
import { getAutomationsById, getSeniorityPreviewUser, getSeniorityPreviewUserQuota } from '../../graphql/custom-queries'
import IntlMessages from '../../util/IntlMessages'
import CircularProgress from '../circular-progress'
import { notificationStore } from '../../context/notificationsContext/store'
import { openSupportChat } from '../../util/open-support-chat'
import SeniorityEntitlementPreview from '../seniority-entitlement-preview'
import { ExpandableRadioGroup } from '../ui/expandable-card'
import { pickScopedLocation } from '../../util/seniority-entitlement-preview'

import { IAutomationSeniorityEntitlementForm } from '@vacationtracker/shared/types/automations'
import { IGetLocationsShort } from '../../types/locations'
import { IGetAutomationsByIdData, IGetSeniorityPreviewUser, IGetSeniorityPreviewUserQuota, ILeaveTypeListShort, ISenitorityAutomation } from '../../types/custom-queries'
import { FrontendUrls } from '../../types/urls'
import { AddonsStatus, AddonsStatusEnum } from '@vacationtracker/shared/types/addons'
import { DEFAULT_WORKING_HOURS_IN_DAY } from '@vacationtracker/shared/data/app-parameters'
import { QuotaUnits } from '@vacationtracker/shared/types/leave-policy'

const { Text, Title } = Typography
const { Option } = Select

interface ISeniorityEntitlementForm {
  locations: IGetLocationsShort[]
  optionLocations: IGetLocationsShort[]
  leaveTypes: ILeaveTypeListShort[]
  existingNames?: string[]
  usedLocationNames?: Record<string, string>
  backToAddons: () => void
  automationId?: string
  hourlyLeaveAccounting: boolean
  amIAdmin: boolean
  status?: AddonsStatus
}

const SeniorityEntitlementForm = ({
  locations,
  optionLocations,
  leaveTypes,
  existingNames = [],
  usedLocationNames = {},
  backToAddons,
  automationId,
  hourlyLeaveAccounting,
  amIAdmin,
  status,
}: ISeniorityEntitlementForm) => {
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { notification } = App.useApp()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)

  const [applyToAllUsers, setApplyToAllUsers] = useState(optionLocations.length === locations.length)
  const [isLoading, setIsLoading] = useState(true)
  const [btnLoading, setBtnLoading] = useState(false)
  const [showPeriodError, setShowPeriodError] = useState(false)
  const [automationSetInHours, setAutomationSetInHours] = useState(hourlyLeaveAccounting)
  const [addon, setAddon] = useState<IAutomationSeniorityEntitlementForm>({
    automationId: '',
    name: formatMessage({ id: 'components.seniorityEntitlement.defaultName' }),
    leaveTypeId: leaveTypes[0]?.id ?? '',
    calculateFrom: 'USER_EMPLOYMENT_DATE',
    awardSeniorityEntitlementDaysOn: 'USER_WORK_ANNIVERSARY',
    applyToAllUsers: optionLocations.length === locations.length,
    isActive: true,
    prorate: false,
    locations: [],
    teams: [],
    labels: [],
    periods: [],
  })
  const [automationPeriods, setAutomationPeriods] = useState<IAutomationSeniorityEntitlementForm['periods']>([])
  const [disableProrate, setDisableProrate] = useState(false)
  const [disableAwardSeniorityEntitlement, setDisableAwardSeniorityEntitlement] = useState(false)
  const scheduleCardRef = useRef<HTMLDivElement>(null)
  const [getAutomationsByIdQuery] = useManualQuery<IGetAutomationsByIdData, { id: string }>(getAutomationsById)
  const [getSeniorityPreviewUserQuery] = useManualQuery<IGetSeniorityPreviewUser, { locationIds?: string[] }>(getSeniorityPreviewUser)
  const [getSeniorityPreviewUserQuotaQuery] = useManualQuery<IGetSeniorityPreviewUserQuota, { id: string }>(getSeniorityPreviewUserQuota)

  const [previewEmployees, setPreviewEmployees] = useState<IGetSeniorityPreviewUser['getUsersFiltered']['users']>([])

  const fetchUserQuota = useCallback(
    async (userId: string) => {
      const response = await getSeniorityPreviewUserQuotaQuery({ variables: { id: userId } })
      const leaveTypes = response.data?.getUser?.leaveDays?.[0]?.leaveTypes ?? []
      return leaveTypes.reduce(
        (acc, leaveType) => {
          acc[leaveType.leaveTypeId] = { days: leaveType.quotas.baseQuota.days, hours: leaveType.quotas.baseQuota.hours }
          return acc
        },
        {} as Record<string, { days: number; hours: number }>
      )
    },
    [getSeniorityPreviewUserQuotaQuery]
  )

  const calculateFromValue = Form.useWatch('calculateFrom', form)
  const awardOnValue = Form.useWatch('awardSeniorityEntitlementDaysOn', form)
  const prorateValue = Form.useWatch('prorate', form)
  const leaveTypeIdValue = Form.useWatch('leaveTypeId', form)
  const selectedLocationValue = Form.useWatch('locations', form)

  const isReadOnly = Boolean(automationId) || optionLocations.length === 0 || status === AddonsStatusEnum.CANCELED || !amIAdmin

  useEffect(() => {
    if (automationId) {
      fetchAddons(automationId)
    } else {
      setIsLoading(false)
    }
  }, [automationId])

  const selectedLocationIds = useMemo(() => {
    if (Array.isArray(selectedLocationValue)) return selectedLocationValue as string[]
    if (typeof selectedLocationValue === 'string') return [selectedLocationValue]
    return []
  }, [selectedLocationValue])

  // The location whose fiscal-year settings drive the preview and the split option.
  const previewLocation = useMemo(() => {
    if (!applyToAllUsers && selectedLocationIds.length > 0) {
      return pickScopedLocation(selectedLocationIds, locations)
    }
    return locations.find((location) => location.isDefault) ?? locations[0]
  }, [applyToAllUsers, selectedLocationIds, locations])

  useEffect(() => {
    if (!applyToAllUsers && selectedLocationIds.length === 0) {
      setPreviewEmployees([])
      return
    }
    fetchPreviewEmployees(!applyToAllUsers ? selectedLocationIds : undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyToAllUsers, selectedLocationIds.join(',')])

  const fetchPreviewEmployees = async (locationIds?: string[]) => {
    try {
      const response = await getSeniorityPreviewUserQuery({ variables: { locationIds } })
      setPreviewEmployees(response.data?.getUsersFiltered?.users ?? [])
    } catch (err) {
      logger.error('ERROR FETCH PREVIEW USERS', err)
    }
  }

  const fetchAddons = async (id) => {
    try {
      const response = await getAutomationsByIdQuery({ variables: { id } })
      if (!response.data || response.error) throw response.error
      const automation = response.data.getAutomation as ISenitorityAutomation
      setAutomationSetInHours(automation.periods[0].units === 'hours')
      const applyToAllUsers = automation?.locations.length === locations.length
      form.setFieldsValue({ applyToAllUsers })
      form.setFieldsValue({ leaveTypeId: automation.leaveTypeId })
      setApplyToAllUsers(applyToAllUsers)

      setAddon({
        automationId: automation.id,
        name: automation.name,
        leaveTypeId: automation.leaveTypeId,
        calculateFrom: automation?.calculateFrom || 'TODAY',
        awardSeniorityEntitlementDaysOn: automation?.awardSeniorityEntitlementDaysOn || 'USER_WORK_ANNIVERSARY',
        applyToAllUsers: applyToAllUsers,
        isActive: automation.isActive,
        locations: automation.locations,
        teams: automation.teams,
        labels: automation.labels,
        periods: automation.periods,
        prorate: automation.prorate,
      } as IAutomationSeniorityEntitlementForm)

      setAutomationPeriods(automation.periods)

      setIsLoading(false)
    } catch (err) {
      logger.error('ERROR FETCH DATA', err)
    }
  }

  const onFinish = () => {
    if (status === AddonsStatusEnum.CANCELED || !amIAdmin) return

    form
      .validateFields()
      .then(async (values) => {
        setBtnLoading(true)
        let response
        try {
          const data = {
            name: values.name,
            leaveTypeId: values.leaveTypeId,
            calculateFrom: values.calculateFrom,
            periods: values.periods,
            locations: values.applyToAllUsers ? locations.map((location) => location.id) : (values.locations ?? []),
            // The prorate Switch is only mounted under the work-anniversary award option, so validateFields
            // omits it (leaving it undefined) for beginning-of-fiscal-year - where prorate is meaningless anyway.
            prorate: values.awardSeniorityEntitlementDaysOn === 'USER_WORK_ANNIVERSARY' ? Boolean(values.prorate) : false,
            awardSeniorityEntitlementDaysOn: values.awardSeniorityEntitlementDaysOn,
          }

          const isExistSameYear = uniqBy(data.periods as IAutomationSeniorityEntitlementForm['periods'], 'years').length !== data.periods.length
          if (isExistSameYear) {
            throw new Error('components.seniorityEntitlement.sameYearError')
          }

          const isSortedDataPeriodsByYear = data.periods.every((period, index) => {
            if (index === 0) return true
            return period.years > data.periods[index - 1].years
          })
          if (!isSortedDataPeriodsByYear) {
            throw new Error('components.seniorityEntitlement.notSortedYearInPeriodError')
          }

          if (!automationId) {
            response = await Api.post('/core/event', {
              eventType: 'SENIORITY_ENTITLEMENT_CREATED',
              eventGroup: 'AUTOMATION',
              ...data,
            })
          } else {
            response = await Api.post('/core/event', {
              eventType: 'SENIORITY_ENTITLEMENT_UPDATED',
              eventGroup: 'AUTOMATION',
              automationId,
              ...data,
              isActive: true,
            })
          }

          notification.open({
            key: response.correlationId,
            message: formatMessage({ id: automationId ? 'automations.seniorityEntitlement.updateInProgress' : 'automations.seniorityEntitlement.createInProgress' }),
            icon: <LoadingOutlined />,
            duration: 0,
          })
          setActionNotifications([...actionNotifications, response.correlationId])

          setTimeout(() => {
            // It's not necessary, but it looks nicer if it waits a bit because in 90% of cases automation creation will be completed by then,
            // and it will immediately appear in the view section.
            history.push(`${FrontendUrls.addons}/seniority-entitlement/view`)
          }, 700)
        } catch (error) {
          setBtnLoading(false)
          if (error.response?.data?.message) {
            notification.error({
              message: formatMessage({ id: 'error.generic' }),
              description: formatMessage({ id: error.response.data.message }),
              duration: 0,
            })
          } else if (typeof error?.message === 'string') {
            notification.error({
              message: formatMessage({ id: 'error.generic' }),
              description: formatMessage({ id: error.message }),
              duration: 0,
            })
          } else {
            const description = response?.correlationId ? formatMessage({ id: 'app.correlationIdError' }, { correlationId: response.correlationId }) : JSON.stringify(error)

            notification.error({
              message: formatMessage({ id: 'error.generic' }),
              description,
              duration: 0,
            })
          }
        }
      })
      .catch((info) => {
        logger.warning('Validate Failed:', info)
        setBtnLoading(false)
        setShowPeriodError(true)
      })
  }

  const handleApplyToAllUsers = () => {
    const nextApplyToAllUsers = !applyToAllUsers
    setApplyToAllUsers(nextApplyToAllUsers)
    if (nextApplyToAllUsers) {
      setDisableAwardSeniorityEntitlement(false)
      setDisableProrate(false)
    }
  }

  const validatePeriods = (periods) => {
    // No tiers: the Form.List rule shows the error; just scroll it into view.
    if (!periods || periods.length === 0) {
      scheduleCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (periods[periods.length - 1].amount === undefined || periods[periods.length - 1].years === undefined) {
      setShowPeriodError(true)
    }
  }

  const onChangePeriodYears = (value, index) => {
    const newAutomationPeriods = automationPeriods.map((period, i) => (i === index ? { ...period, years: value } : period))
    setAutomationPeriods(newAutomationPeriods)
  }

  const onChangePeriod = (value, index) => {
    const newAutomationPeriods = automationPeriods.map((period, i) =>
      i === index ? { ...period, amount: value, units: automationSetInHours ? 'hours' : ('days' as QuotaUnits) } : period
    )
    setAutomationPeriods(newAutomationPeriods)
  }

  const onChangeLocation = (locationIds: string[]) => {
    const hasUserYearLocation = locationIds.some((id) => locations.find((location) => location.id === id)?.resetQuotas === 'USER_YEAR')
    if (hasUserYearLocation) {
      form.setFieldsValue({ awardSeniorityEntitlementDaysOn: 'USER_WORK_ANNIVERSARY' })
      setDisableAwardSeniorityEntitlement(true)
      setDisableProrate(true)
      form.setFieldsValue({ prorate: false })
    } else {
      setDisableAwardSeniorityEntitlement(false)
      setDisableProrate(false)
    }
  }

  const onChangeAwardDaysOn = (value) => {
    if (value === 'BEGINNING_OF_THE_FISCAL_YEAR') {
      setDisableProrate(true)
      form.setFieldsValue({ prorate: false })
    } else {
      setDisableProrate(false)
    }
  }

  const openCrisp = (e) => {
    e.preventDefault()
    openSupportChat()
  }

  let minNumber = 1
  const currentPeriods = form.getFieldValue('periods')
  if (currentPeriods?.length > 1) {
    minNumber = currentPeriods[currentPeriods.length - 1].years as number
  } else {
    minNumber = 1
  }

  // Locations already used by another SBE automation - shown but disabled (one automation per location).
  const usedLocationIds = locations.filter((location) => !optionLocations.some((option) => option.id === location.id)).map((location) => location.id)
  // Some (but not necessarily all) locations are taken → "All users" can't cover everyone.
  const someLocationsTaken = !automationId && optionLocations.length !== locations.length

  const isLocationUserYear = !applyToAllUsers && previewLocation?.resetQuotas === 'USER_YEAR'
  const isSplitDisabled = disableProrate || awardOnValue === 'BEGINNING_OF_THE_FISCAL_YEAR' || isLocationUserYear || isReadOnly

  const validPreviewPeriods = automationPeriods.filter((period) => typeof period?.amount === 'number' && typeof period?.years === 'number')
  const selectedLeaveTypeName = leaveTypes.find((leaveType) => leaveType.id === leaveTypeIdValue)?.name

  const stepHeader = (stepNumber: number, titleId: string, descriptionId?: string) => (
    <div className="sbe-step-header">
      <div className="sbe-step-title">
        <span className="sbe-step-badge">{stepNumber}</span>
        <Title level={5} style={{ margin: 0 }}>
          <IntlMessages id={titleId} />
        </Title>
      </div>
      {descriptionId && (
        <Text type="secondary" className="sbe-step-description">
          <IntlMessages id={descriptionId} />
        </Text>
      )}
    </div>
  )

  return (
    <>
      {isLoading && automationId ? (
        <CircularProgress />
      ) : (
        <>
          {optionLocations.length === 0 && <Alert message={<IntlMessages id="components.seniorityEntitlement.noLocations" />} type="warning" style={{ marginBottom: 20 }} />}
          <div style={{ marginBottom: 16 }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              <IntlMessages id="components.seniorityEntitlement.newAutomationTitle" />
            </Title>
            <Text type="secondary">
              <IntlMessages id="components.seniorityEntitlement.newAutomationSubtitle" />
            </Text>
          </div>

          <Alert
            type="warning"
            className="sbe-startdate-warning"
            style={{ marginBottom: 20 }}
            icon={<ScheduleOutlined />}
            showIcon
            message={
              <IntlMessages
                id="components.seniorityEntitlement.startDateWarning"
                values={{
                  b: (...chunks) => <b>{chunks}</b>,
                  link: (...chunks) => <Link to="/app/users">{chunks}</Link>,
                }}
              />
            }
          />

          <Form
            layout="vertical"
            form={form}
            name="automationsForm"
            initialValues={addon}
            onFinish={onFinish}
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            requiredMark={false}
            disabled={isReadOnly}
            className="automation-seniority-entitlement-form"
            onFinishFailed={(error) => {
              validatePeriods(error.values.periods)
            }}
          >
            <Row gutter={24}>
              <Col xs={24} lg={14}>
                <Card className="sbe-step-card">
                  {stepHeader(1, 'components.seniorityEntitlement.step1Title')}
                  <Form.Item
                    label={<IntlMessages id="components.seniorityEntitlement.automationName" />}
                    name="name"
                    rules={[
                      { required: true, message: <IntlMessages id="form.inputRequired" /> },
                      {
                        validator: async (_, value) => {
                          const trimmed = (value || '').trim().toLowerCase()
                          if (trimmed && existingNames.some((existing) => existing.trim().toLowerCase() === trimmed)) {
                            return Promise.reject(new Error(formatMessage({ id: 'components.seniorityEntitlement.nameExists' })))
                          }
                        },
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item label={<IntlMessages id="components.seniorityEntitlement.whoDoesThisApplyTo" />} name="applyToAllUsers">
                    <ExpandableRadioGroup
                      disabled={isReadOnly}
                      onSelect={handleApplyToAllUsers}
                      options={[
                        {
                          value: true,
                          icon: <TeamOutlined />,
                          title: <IntlMessages id="components.seniorityEntitlement.allUsers" />,
                          disabled: someLocationsTaken,
                          description: someLocationsTaken ? (
                            <IntlMessages id="components.seniorityEntitlement.allUsersDisabledReason" />
                          ) : (
                            <IntlMessages id="components.seniorityEntitlement.allUsersDescription" />
                          ),
                        },
                        {
                          value: false,
                          icon: <EnvironmentOutlined />,
                          title: <IntlMessages id="components.seniorityEntitlement.aSegment" />,
                          description: <IntlMessages id="components.seniorityEntitlement.aSegmentDescription" />,
                          expandContent: (
                            <>
                              <Form.Item
                                label={<IntlMessages id="app.locations" />}
                                rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                                name="locations"
                                className="sbe-segment-location"
                              >
                                <Select
                                  mode="multiple"
                                  allowClear
                                  style={{ width: '100%' }}
                                  placeholder={<IntlMessages id="components.seniorityEntitlement.selectLocations" />}
                                  optionFilterProp="label"
                                  onChange={onChangeLocation}
                                  options={locations.map((location) => ({
                                    value: location.id,
                                    label: location.name,
                                    disabled: !automationId && usedLocationIds.includes(location.id),
                                  }))}
                                  optionRender={(option) => {
                                    const usedByName = !automationId ? usedLocationNames[String(option.value)] : undefined
                                    return (
                                      <span className="sbe-loc-option">
                                        <span>{option.label}</span>
                                        {usedByName && (
                                          <span className="sbe-loc-option-used">
                                            <IntlMessages id="components.seniorityEntitlement.alreadyInAutomation" values={{ name: usedByName }} />
                                          </span>
                                        )}
                                      </span>
                                    )
                                  }}
                                />
                              </Form.Item>
                              {usedLocationIds.length > 0 && (
                                <Text type="secondary" className="sbe-inline-note">
                                  <IntlMessages id="components.seniorityEntitlement.oneAutomationPerLocation" />
                                </Text>
                              )}
                            </>
                          ),
                        },
                      ]}
                    />
                  </Form.Item>
                </Card>

                <Card className="sbe-step-card">
                  {stepHeader(2, 'components.seniorityEntitlement.step2Title')}
                  <Form.Item name="leaveTypeId" rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}>
                    <Select showSearch style={{ width: '100%' }} placeholder={<IntlMessages id="app.pleaseSelect" />} optionFilterProp="label">
                      {leaveTypes.map((leaveType) => (
                        <Option value={leaveType.id} key={leaveType.id} label={leaveType.name}>
                          <span className="sbe-leave-option">
                            <span className="sbe-leave-dot" style={{ backgroundColor: leaveType.color || 'var(--sbe-neutral-dot)' }} />
                            {leaveType.name}
                          </span>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Card>

                <Card className="sbe-step-card" ref={scheduleCardRef}>
                  {stepHeader(3, 'components.seniorityEntitlement.step3Title', 'components.seniorityEntitlement.step3Description')}
                  <Form.List
                    name="periods"
                    rules={[
                      {
                        validator: async (_, periods) => {
                          if (!periods || periods.length === 0) {
                            return Promise.reject(new Error(formatMessage({ id: 'components.seniorityEntitlement.addAtLeastOneTier' })))
                          }
                        },
                      },
                    ]}
                  >
                    {(fields, { remove }, { errors }) => {
                      return (
                        <>
                          {fields.length > 0 && (
                            <div className="sbe-schedule-head">
                              <span>
                                <IntlMessages id="components.seniorityEntitlement.afterYears" />
                              </span>
                              <span>
                                {automationSetInHours ? (
                                  <IntlMessages id="components.seniorityEntitlement.grantHours" />
                                ) : (
                                  <IntlMessages id="components.seniorityEntitlement.grantDays" />
                                )}
                              </span>
                              <span />
                            </div>
                          )}
                          {fields.map((field, index) => {
                            return (
                              <div className="sbe-schedule-row" key={field.key}>
                                <Form.Item noStyle name={[field.name, 'years']} rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}>
                                  <InputNumber
                                    defaultValue={minNumber}
                                    min={minNumber}
                                    max={100}
                                    step={1}
                                    style={{ width: '100%' }}
                                    parser={(value) => Math.round(Number(value) || minNumber)}
                                    onChange={(value) => onChangePeriodYears(value, index)}
                                    disabled={fields.length - 1 !== index || Boolean(automationId) || optionLocations.length === 0}
                                  />
                                </Form.Item>
                                <Form.Item noStyle name={[field.name, 'amount']} rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}>
                                  <InputNumber
                                    min={0.5}
                                    max={automationSetInHours ? 2920 : 365}
                                    step={0.5}
                                    style={{ width: '100%' }}
                                    prefix="+"
                                    parser={(value) => Math.round(Number(value) * 2) / 2}
                                    onChange={(value) => onChangePeriod(value, index)}
                                  />
                                </Form.Item>
                                <div className="sbe-schedule-actions">
                                  {fields.length > 1 && !isReadOnly && (
                                    <MinusCircleOutlined
                                      onClick={() => {
                                        remove(field.name)
                                        const periods = form.getFieldValue('periods')
                                        setAutomationPeriods(periods as IAutomationSeniorityEntitlementForm['periods'])
                                        const lastPeriod = periods[periods.length - 2]
                                        minNumber = lastPeriod?.years ? (lastPeriod.years as number) + 1 : 1
                                        setShowPeriodError(false)
                                      }}
                                      style={{ color: 'red' }}
                                    />
                                  )}
                                </div>
                              </div>
                            )
                          })}
                          {!isReadOnly && fields.length < 20 && (
                            <Button
                              type="dashed"
                              block
                              className="sbe-add-tier"
                              icon={<PlusCircleOutlined />}
                              onClick={() => {
                                const periods = form.getFieldValue('periods') || []
                                if (periods.length > 0) {
                                  const lastPeriod = periods[periods.length - 1]
                                  if (!lastPeriod || lastPeriod.amount === undefined || lastPeriod.years === undefined) {
                                    setShowPeriodError(true)
                                    return
                                  }
                                }
                                const nextYears = periods.length > 0 ? (periods[periods.length - 1].years as number) + 1 : 1
                                minNumber = nextYears

                                const newPeriods = [
                                  ...periods,
                                  {
                                    years: nextYears,
                                    amount: 1 * (automationSetInHours ? DEFAULT_WORKING_HOURS_IN_DAY : 1),
                                    units: automationSetInHours ? 'hours' : 'days',
                                  },
                                ]
                                setAutomationPeriods(newPeriods)
                                form.setFieldsValue({ periods: newPeriods })
                                setShowPeriodError(false)
                              }}
                            >
                              <IntlMessages id={fields.length === 0 ? 'components.seniorityEntitlement.addFirstTier' : 'components.seniorityEntitlement.addTier'} />
                            </Button>
                          )}
                          {errors.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                              <Text type="danger">{errors}</Text>
                            </div>
                          )}
                        </>
                      )
                    }}
                  </Form.List>
                  {showPeriodError && automationPeriods.length > 0 && (
                    <Text type="danger">
                      <br />
                      <IntlMessages id="components.seniorityEntitlement.allFiledsAreRequired" />
                    </Text>
                  )}
                </Card>

                <Card className="sbe-step-card">
                  {stepHeader(4, 'components.seniorityEntitlement.step4Title', 'components.seniorityEntitlement.step4Description')}
                  <Form.Item name="calculateFrom" rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}>
                    <ExpandableRadioGroup
                      disabled={isReadOnly}
                      options={[
                        {
                          value: 'TODAY',
                          icon: <ClockCircleOutlined />,
                          title: <IntlMessages id="components.seniorityEntitlement.fromTodayTitle" />,
                          description: <IntlMessages id="components.seniorityEntitlement.fromTodayDescription" />,
                        },
                        {
                          value: 'USER_EMPLOYMENT_DATE',
                          icon: <HistoryOutlined />,
                          title: <IntlMessages id="components.seniorityEntitlement.fromStartDateTitle" />,
                          description: <IntlMessages id="components.seniorityEntitlement.fromStartDateDescription" />,
                        },
                      ]}
                    />
                  </Form.Item>
                </Card>

                <Card className="sbe-step-card">
                  {stepHeader(5, 'components.seniorityEntitlement.step5Title')}
                  <Form.Item name="awardSeniorityEntitlementDaysOn" rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}>
                    <ExpandableRadioGroup
                      disabled={isReadOnly}
                      onSelect={(value) => onChangeAwardDaysOn(value)}
                      options={[
                        {
                          value: 'BEGINNING_OF_THE_FISCAL_YEAR',
                          icon: <CalendarOutlined />,
                          title: <IntlMessages id="components.seniorityEntitlement.beginningFiscalYearTitle" />,
                          description: <IntlMessages id="components.seniorityEntitlement.beginningFiscalYearDescription" />,
                          disabled: disableAwardSeniorityEntitlement || Boolean(automationId) || optionLocations.length === 0,
                        },
                        {
                          value: 'USER_WORK_ANNIVERSARY',
                          icon: <FieldTimeOutlined />,
                          title: <IntlMessages id="components.seniorityEntitlement.workAnniversaryTitle" />,
                          description: <IntlMessages id="components.seniorityEntitlement.workAnniversaryDescription" />,
                          expandContent: (
                            <>
                              <div className="sbe-split-header">
                                <Form.Item name="prorate" valuePropName="checked" noStyle>
                                  <Switch size="small" disabled={isSplitDisabled} />
                                </Form.Item>
                                <span className="sbe-split-title">
                                  <IntlMessages id="components.seniorityEntitlement.splitAwardTitle" />
                                </span>
                              </div>
                              <Text type="secondary" className="sbe-split-desc">
                                <IntlMessages id="components.seniorityEntitlement.splitAwardDescription" />
                              </Text>
                              {isLocationUserYear && previewLocation && (
                                <div className="sbe-split-warning">
                                  <WarningOutlined />{' '}
                                  <IntlMessages
                                    id="components.seniorityEntitlement.splitNotAvailable"
                                    values={{ location: previewLocation.name, b: (...chunks) => <b>{chunks}</b> }}
                                  />
                                </div>
                              )}
                              {prorateValue && !isSplitDisabled && (
                                <div className="sbe-split-example">
                                  <IntlMessages id="components.seniorityEntitlement.splitExample" values={{ c: (...chunks) => <code className="sbe-mono">{chunks}</code> }} />
                                </div>
                              )}
                            </>
                          ),
                        },
                      ]}
                    />
                  </Form.Item>
                </Card>

                <div className="sbe-actions">
                  <Button type="default" style={{ marginRight: 10 }} onClick={backToAddons}>
                    <IntlMessages id="app.cancel" />
                  </Button>
                  <Button type="primary" loading={btnLoading} htmlType="submit">
                    {automationId ? <IntlMessages id="app.update" /> : <IntlMessages id="components.seniorityEntitlement.createAutomation" />}
                  </Button>
                </div>
              </Col>

              <Col xs={24} lg={10}>
                <div className="sbe-preview-sticky">
                  <SeniorityEntitlementPreview
                    employees={previewEmployees}
                    periods={validPreviewPeriods.map((period) => ({ years: period.years, amount: period.amount, units: period.units }))}
                    units={automationSetInHours ? 'hours' : 'days'}
                    calculateFrom={calculateFromValue}
                    awardSeniorityEntitlementDaysOn={awardOnValue}
                    prorate={prorateValue}
                    leaveTypeName={selectedLeaveTypeName}
                    leaveTypeId={leaveTypeIdValue}
                    location={previewLocation}
                    fetchUserQuota={fetchUserQuota}
                  />
                </div>
              </Col>
            </Row>
          </Form>

          {Boolean(automationId) && (
            <Alert
              style={{ marginTop: 12 }}
              message={<IntlMessages id="components.seniorityEntitlement.disableEditInfo" values={{ link: (...chunks) => <a onClick={openCrisp}>{chunks}</a> }} />}
              type="warning"
            />
          )}
        </>
      )}
    </>
  )
}
export default SeniorityEntitlementForm
